import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent } from '@mui/material';
import SEO from '../components/SEO';
import Footer from '../components/landing/Footer';
import FAQSection from '../components/landing/FAQSection';
import { PAGE_CONTAINER_SX } from '../components/landing/pageLayout';

const WHAT_WE_DO_FAQS = [
  {
    question: "What types of code issues does Sniffr.ai detect?",
    answer: "Sniffr.ai detects a wide range of issues including logic errors, security vulnerabilities (OWASP Top 10), performance anti-patterns, missing error handling, requirement mismatches, incomplete acceptance criteria coverage, architectural violations, code duplication, and insufficient test coverage. Each issue is classified by severity: critical, high, medium, and low.",
  },
  {
    question: "How is Sniffr.ai different from other code review tools?",
    answer: "Unlike linters and static analysis tools that only check syntax and style, Sniffr.ai understands the business context of your code. It validates that each pull request satisfies the linked JIRA requirements and acceptance criteria, reviews architecture decisions, and generates executive-level engineering reports — going far beyond code quality checks.",
  },
  {
    question: "Can Sniffr.ai validate requirements from JIRA automatically?",
    answer: "Yes. Sniffr.ai integrates directly with JIRA and can link pull requests to their corresponding user stories and epics. It then validates that the code implementation fully satisfies every acceptance criterion in the linked ticket, flagging gaps and mismatches before code is merged.",
  },
  {
    question: "What is engineering accuracy scoring?",
    answer: "Engineering accuracy scoring is Sniffr.ai's proprietary metric that measures how well a pull request satisfies its requirements, acceptance criteria, and architectural standards. Scores are tracked over time per developer, team, and project — giving engineering leaders visibility into code quality trends without manual review overhead.",
  },
  {
    question: "Does Sniffr.ai work on legacy codebases?",
    answer: "Yes. Sniffr.ai works on any codebase regardless of age or technology stack. It analyzes the diff of each pull request, so it improves quality incrementally without requiring a full audit of your existing code. Legacy codebases often benefit the most from requirement validation since documentation is sparse and institutional knowledge is siloed.",
  },
  {
    question: "What ROI can teams expect from Sniffr.ai?",
    answer: "Teams typically see 75% reduction in UAT effort, 80% fewer defects reaching QA, 40–60% faster PR review cycles, and significant reduction in production incidents. The ROI is highest for teams with complex requirements, multiple integrations, or compliance obligations where defect costs are high.",
  },
];

const WhatWeDo = () => {
  return (
    <>
      <SEO
        title="What We Do | Sniffr.ai - AI Code Review & Requirement Validation"
        description="Sniffr.ai provides AI code review, AI requirement validation, AI UAT automation, PR summarization, engineering accuracy scoring, and enterprise dashboards. Catch 80% of defects before QA."
        keywords="AI code review, AI requirement validation, AI UAT automation, AI PR summarization, engineering accuracy scoring, enterprise dashboards, UAT automation, requirement validation"
        url="/what-we-do"
      />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
        <Box component="main" sx={{ flex: 1, py: { xs: 6, sm: 8, md: 10 } }}>
          <Container maxWidth="xl" sx={PAGE_CONTAINER_SX}>
            {/* Hero Section */}
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '32px', md: '48px' },
                  fontWeight: 600,
                  mb: 3,
                  color: '#141414',
                  fontFamily: 'MD IO, monospace'
                }}
              >
                What We Do
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '20px', md: '24px' },
                  fontWeight: 400,
                  color: '#616161',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontFamily: 'Segoe UI, monospace',
                  lineHeight: 1.6
                }}
              >
                Sniffr.ai is an AI-powered UAT Automation Engine that reviews code, requirements, architecture, and acceptance criteria—catching 80% of defects before QA and reducing UAT effort by 75%.
              </Typography>
            </Box>

            {/* Features Grid */}
            <Grid container spacing={4} sx={{ mb: 8 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '24px',
                        fontWeight: 600,
                        mb: 2,
                        color: '#141414',
                        fontFamily: 'MD IO, monospace'
                      }}
                    >
                      AI Code Review
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#616161',
                        lineHeight: 1.7,
                        fontFamily: 'Segoe UI, monospace'
                      }}
                    >
                      Our AI-powered code review engine analyzes pull requests with deep understanding of code patterns, architecture, and best practices. Unlike traditional linting tools, Sniffr.ai understands context, identifies performance bottlenecks, detects architectural flaws, and enforces coding standards across your entire codebase. It provides actionable feedback that helps developers write better code faster.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '24px',
                        fontWeight: 600,
                        mb: 2,
                        color: '#141414',
                        fontFamily: 'MD IO, monospace'
                      }}
                    >
                      AI Requirement Validation
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#616161',
                        lineHeight: 1.7,
                        fontFamily: 'Segoe UI, monospace'
                      }}
                    >
                      Sniffr.ai automatically validates that code implementations match JIRA requirements and acceptance criteria. By connecting your code to your project management tools, we ensure that developers, QA, and product teams stay aligned. This eliminates costly rework and ensures that what gets built matches what was specified, catching requirement mismatches before they reach production.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '24px',
                        fontWeight: 600,
                        mb: 2,
                        color: '#141414',
                        fontFamily: 'MD IO, monospace'
                      }}
                    >
                      AI UAT Automation
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#616161',
                        lineHeight: 1.7,
                        fontFamily: 'Segoe UI, monospace'
                      }}
                    >
                      Our UAT automation engine reduces manual testing effort by 75% by automatically validating user acceptance criteria against code implementations. Sniffr.ai reviews code changes in the context of business requirements, catching 80% of defects before they reach QA. This shift-left approach ensures higher quality releases while dramatically reducing the time and cost of user acceptance testing.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '24px',
                        fontWeight: 600,
                        mb: 2,
                        color: '#141414',
                        fontFamily: 'MD IO, monospace'
                      }}
                    >
                      AI PR Summarization
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#616161',
                        lineHeight: 1.7,
                        fontFamily: 'Segoe UI, monospace'
                      }}
                    >
                      Automatically generate comprehensive pull request summaries that highlight key changes, potential impacts, and review priorities. Our AI understands the context of your changes and provides intelligent summaries that help reviewers focus on what matters most. This accelerates code review cycles and ensures nothing important gets missed.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '24px',
                        fontWeight: 600,
                        mb: 2,
                        color: '#141414',
                        fontFamily: 'MD IO, monospace'
                      }}
                    >
                      Engineering Accuracy Scoring
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#616161',
                        lineHeight: 1.7,
                        fontFamily: 'Segoe UI, monospace'
                      }}
                    >
                      Get objective, data-driven scores that measure how well code implementations match requirements, follow best practices, and maintain quality standards. Our engineering accuracy scoring provides visibility into code quality trends, helping teams identify areas for improvement and track progress over time. This enables data-driven decision making for engineering leadership.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '24px',
                        fontWeight: 600,
                        mb: 2,
                        color: '#141414',
                        fontFamily: 'MD IO, monospace'
                      }}
                    >
                      Enterprise Dashboards
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#616161',
                        lineHeight: 1.7,
                        fontFamily: 'Segoe UI, monospace'
                      }}
                    >
                      Comprehensive dashboards provide executive-level insights into engineering health, code quality trends, requirement alignment, and team performance. Track key metrics like defect rates, review cycle times, requirement coverage, and engineering accuracy scores. These dashboards help leadership make informed decisions and identify opportunities for process improvement.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Additional Context Section */}
            <Box sx={{ mt: 8, p: 4, bgcolor: '#f9fafb', borderRadius: 2 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '28px',
                  fontWeight: 600,
                  mb: 3,
                  color: '#141414',
                  fontFamily: 'MD IO, monospace'
                }}
              >
                Why Sniffr.ai is Different
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  color: '#616161',
                  lineHeight: 1.7,
                  mb: 2,
                  fontFamily: 'Segoe UI, monospace'
                }}
              >
                While traditional code review tools focus solely on code quality, Sniffr.ai is a complete UAT Automation Engine that validates the entire software development lifecycle. We don't just review code—we ensure that code matches requirements, validates acceptance criteria, and meets business expectations.
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  color: '#616161',
                  lineHeight: 1.7,
                  fontFamily: 'Segoe UI, monospace'
                }}
              >
                Our AI understands the relationship between requirements, architecture, code, and testing. This holistic approach catches issues that code review alone would miss—like requirement mismatches, missing acceptance criteria, and architectural misalignments. That's why we catch 80% of defects before QA and reduce UAT effort by 75%.
              </Typography>
            </Box>
          </Container>
        </Box>
        <FAQSection faqs={WHAT_WE_DO_FAQS} title="FAQs: What Sniffr.ai Does" />
      </Box>
    </>
  );
};

export default WhatWeDo;

